import classNames from 'classnames';
import { createRef, MouseEvent, ReactElement, RefObject, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { removeOrphans } from '~globals/common';
import { fireOpenModal, firePageViewEvent } from '~globals/tracking';
import CloseIcon from '~images/inline/close.inline.svg';
import { updateFilter, updatePlanType } from '~redux/Actions/GlobalActions';

import * as styles from './ModalDialog.module.css';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';

interface ContentProps {
    heading?: string | ReactElement | null;
    title?: string | ReactElement | null;
    splitLayoutWithImage?: string;
    imageZoom?: string;
    children: ReactElement | ReactElement[];
    disablePadding?: boolean;
    isGeneric?: boolean;
}

// Content component
const ModalPage = ({
    heading,
    title,
    splitLayoutWithImage,
    imageZoom = 'bg-cover',
    children,
    disablePadding,
    isGeneric = false,
}: ContentProps) => {
    const { pathname } = usePageContext().location;

    useEffect(() => {
        fireOpenModal();

        const state = pathname.replace(/^.*\/([^/]+)\/?$/, '$1');
        firePageViewEvent({
            sysEnv: { siteGroup: 'IFP' },
            page: { name: `zip modal: ${state}`, section: 'SEO' },
        });
    }, [])

    const Heading = () => (
        <div className="flex flex-col items-center justify-center w-full px-2 lg:px-12">
            {typeof heading === 'string' ? (
                <h1
                    id="modalTitle"
                    className="font-bold font-poppins text-center text-comms text-3xl md:text-4xl lg:text-5xl mb-3 lg:mb-6 w-full"
                    dangerouslySetInnerHTML={{ __html: removeOrphans(heading) }}
                />
            ) : (
                heading
            )}
            {typeof title === 'string' ? (
                <h2
                    className="font-bold font-poppins text-center text-black text-lg md:text-xl lg:text-2xl w-full"
                    dangerouslySetInnerHTML={{ __html: removeOrphans(title) }}
                />
            ) : (
                title
            )}
        </div>
    );

    const Title = () =>
        title &&
        (typeof title == 'string' ? (
            <h1
                id="modalTitle"
                className={classNames(
                    'font-bold font-poppins text-center text-black text-xl md:text-2xl md:shadow',
                    'flex items-center justify-center',
                    'w-full py-4 pl-4 pr-10 md:px-12'
                )}
                dangerouslySetInnerHTML={{ __html: removeOrphans(title) }}
            />
        ) : (
            title
        ));

    if (!splitLayoutWithImage) {
        return (
            <>
                {heading ? <Heading /> : <Title />}
                <div
                    className={
                        disablePadding
                            ? 'px-0 md:px-0 md:py-0 lg:px-0'
                            : isGeneric
                              ? '!px-0 pt-3 pb-0 md:pt-6 md:pb-6'
                              : 'px-4 md:px-10 md:py-6'
                    }
                >
                    {children}
                </div>
            </>
        );
    } else
        return (
            <div className="flex">
                <div className="p-4 md:px-6 md:py-8">
                    {title && (
                        <h1
                            id="modalTitle"
                            className="text-center font-bold font-poppins md:text-left text-xl md:text-2xl mb-4 md:mb-6 text-comms"
                            dangerouslySetInnerHTML={{ __html: removeOrphans(title as string) }}
                        />
                    )}
                    {children}
                </div>

                <div
                    className={classNames(
                        styles.imageContainer,
                        imageZoom,
                        'hidden md:block bg-no-repeat min-w-80 rounded-r-lg'
                    )}
                    style={{ backgroundImage: `url(${splitLayoutWithImage})` }}
                ></div>
            </div>
        );
};

interface ContainerProps extends ContentProps {
    isGeneric?: boolean;
    isOpen: boolean;
    setIsOpen: (a: boolean) => void;
    className?: string;
    disablePadding?: boolean;
}

// Container component
const Modal = ({
    isGeneric = false,
    isOpen,
    setIsOpen,
    children,
    heading,
    title,
    splitLayoutWithImage,
    imageZoom,
    className,
    disablePadding,
}: ContainerProps) => {
    const container: RefObject<HTMLDivElement> = createRef();
    const dispatch = useDispatch();

    const handleOutsideClick = (e: MouseEvent) => {
        if (container?.current && !container.current.contains(e.target as Node)) {           
            setIsOpen(false);
            dispatch(updatePlanType(''));
        }
    };

    const handleClick = () => {
        setIsOpen(false);
        dispatch(updateFilter(''));
        dispatch(updatePlanType(''));
    };

    const getElement = (e: React.KeyboardEvent | Event, selector: string) => {
        return ((e.target as Element).closest('.modal-dialog') as HTMLDivElement).querySelector(
            selector
        );
    };

    useEffect(() => {
        if (container.current) {
            const target =
                container.current.querySelector('input') ||
                container.current.querySelector('button');
            target?.focus();
            target?.addEventListener('keydown', (e: Event) => {
                if (e instanceof KeyboardEvent && e.key === 'Tab' && e.shiftKey) {
                    e.preventDefault();
                    (getElement(e, '.modal-close') as HTMLButtonElement).focus();
                }
            });
        }
    }, [isOpen]);

    return !isOpen ? null : (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
            onClick={handleOutsideClick}
            className={classNames(
                styles.backdrop,
                className,
                'fixed top-0 left-0 w-full h-screen z-50',
                'flex items-center justify-center px-4 md:px-0'
            )}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    (getElement(e, '.modal-close') as HTMLButtonElement).click();
                }
            }}
            data-qa="ModalBackdrop"
            aria-hidden={isOpen ? 'false' : 'true'}
        >
            <div
                role="dialog"
                data-qa="ModalContainer"
                aria-labelledby="modalTitle"
                aria-modal={isOpen}
                ref={container}
                className={classNames(
                    styles.container,
                    'modal-dialog',
                    'relative w-full md:w-auto max-w-screen-md',
                    'bg-white transition-opacity rounded-lg',
                    'md:my-0 overflow-y-scroll md:overflow-y-auto',
                    isGeneric ? 'pt-12 pb-10 px-6' : ''
                )}
            >
                <ModalPage
                    heading={heading}
                    title={title}
                    splitLayoutWithImage={splitLayoutWithImage}
                    imageZoom={imageZoom}
                    disablePadding={disablePadding}
                    isGeneric={isGeneric}
                >
                    {children}
                </ModalPage>
                {/* for keyboard focus this button comes last */}
                <button
                    data-qa="ModalCloseButton"
                    onClick={handleClick}
                    aria-label="Close dialog"
                    onKeyDown={(e) => {
                        if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            (getElement(e, 'a, input, button, select') as HTMLElement).focus();
                        }
                    }}
                    className="absolute modal-close flex items-center justify-center w-10 h-10 top-2 right-2"
                >
                    <CloseIcon className={styles.closeIcon} />
                </button>
            </div>
        </div>
    );
};

export default Modal;
